import React, { useState, useEffect, useMemo, useRef } from "react";
import apiClient from "../api/client";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { openStockInNewTab } from "../utils/openStock";

const Statistics = ({ theme = 'light' }) => {
  const markets = ['NASDAQ', 'NYSE', 'CRYPTO', 'ETF', 'FOREX', 'COMMODITIES', 'BIST'];
  const [selectedMarket, setSelectedMarket] = useState('NASDAQ');
  const [assetModelSummary, setAssetModelSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, item: null });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/summary/asset-model', {
          params: { category: selectedMarket },
        });
        const data = response.data || {};
        setAssetModelSummary(data || []);
        setCurrentPage(1); // Reset to first page when market changes
      } catch (e) {
        console.error('Failed to load market summary:', e);
        setAssetModelSummary([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedMarket]);

    const tableCls = theme === 'dark' 
      ? 'min-w-full text-sm divide-y divide-gray-700 bg-gray-900'
      : 'min-w-full text-sm divide-y divide-gray-200 bg-white';
    const tbodyCls = theme === 'dark' ? 'bg-gray-900 divide-y divide-gray-800' : 'bg-white divide-y divide-gray-200';

  // Sorting and pagination
  const sorted = useMemo(() => {
    const rows = [...assetModelSummary];
    const getVal = (row, field) => {
      switch (field) {
        case 'symbol': return row.symbol || '';
        case 'model_name': return row.model_name || '';
        case 'period_roi': return Number(row.period_roi) || 0;
        case 'price_change_pct': return Number(row.price_change_pct) || 0;
        case 'period_alpha': return Number(row.period_alpha) || 0;
        case 'model_forward_trend_accuracy': return Number(row.model_forward_trend_accuracy) || 0;
        case 'model_next_day_direction_accuracy': return Number(row.model_next_day_direction_accuracy) || 0;
        case 'created_at': {
          let v = row.created_at;
          if (v && typeof v === 'object' && v.$date) v = v.$date;
          const t = Date.parse(v);
          return Number.isFinite(t) ? t : 0;
        }
        default: return '';
      }
    };
    rows.sort((a, b) => {
      const av = getVal(a, sortField);
      const bv = getVal(b, sortField);
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDirection === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [assetModelSummary, sortField, sortDirection]);

  const totalItems = sorted.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sorted.slice(startIndex, endIndex);

  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handleItemsPerPageChange = (n) => { setItemsPerPage(n); setCurrentPage(1); };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
      : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
  };

  const formatCreatedAt = (val) => {
    try {
      let v = val;
      if (!v) return '-';
      if (typeof v === 'object' && v.$date) v = v.$date;
      if (typeof v === 'string') {
        v = v.replace(/\.(\d{3})\d+/, '.$1');
        if (/^\d{4}-\d{2}-\d{2}T/.test(v) && !/[zZ]|[+\-]\d{2}:?\d{2}$/.test(v)) {
          v = v + 'Z';
        }
      }
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(val);
      const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', timeZone: 'Europe/Berlin' }).format(d);
      const timeStr = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin' }).format(d);
      return `${dateStr}, ${timeStr}`;
    } catch {
      return '-';
    }
  };

  const formatNumber = (val, digits = 2) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'string') {
      const s = val.trim();
      if (s === '') return '-';
      if (s.toUpperCase() === 'N/A') return 'N/A';
    }
    const num = Number(val);
    if (Number.isFinite(num)) return num.toFixed(digits);
    return typeof val === 'string' ? val : '-';
  };

  // Close context menu on outside click, ESC, or scroll
  useEffect(() => {
    if (!ctxMenu.open) return;
    const onDown = (e) => {
      if (!menuRef.current) return setCtxMenu({ open: false, x: 0, y: 0, item: null });
      if (!menuRef.current.contains(e.target)) setCtxMenu({ open: false, x: 0, y: 0, item: null });
    };
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu({ open: false, x: 0, y: 0, item: null }); };
    const onScroll = () => setCtxMenu({ open: false, x: 0, y: 0, item: null });
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu.open]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Model Performance Summary (12 months)</h2>
      <h4><strong>Important: Simulated performance</strong></h4>
      <p className="text-sm mb-4" style={{ color: theme === 'dark' ? '#999' : '#666' }}>
        {' '}The figures shown below are based on historical backtesting and are <strong>not live trading results</strong>. They are hypothetical and do not predict future performance. Past or simulated past performance is not a reliable indicator of future results.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {markets.map((m) => (
          <button key={m} onClick={() => setSelectedMarket(m)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              selectedMarket === m ? 'bg-blue-600 text-white'
              : theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
            }`}>
            {m.toUpperCase()}
          </button>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <label htmlFor="items-per-page" className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Show:
            </label>
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`block w-20 px-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-600 text-gray-300'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>All</option>
            </select>
            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              entries
            </span>
          </div>
        </div>
        <div ref={containerRef} className="relative">
          <div className={`overflow-x-auto rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <table className={tableCls}>
              <thead className="bg-gray-100 dark:bg-gray-800 text-[75%]">
                <tr>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('symbol')}>
                    <div className="flex items-center justify-center gap-1">Symbol {getSortIcon('symbol')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('model_name')}>
                    <div className="flex items-center justify-center gap-1">Model {getSortIcon('model_name')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('period_roi')}>
                    <div className="flex items-center justify-center gap-1">Period ROI (%) {getSortIcon('period_roi')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('price_change_pct')}>
                    <div className="flex items-center justify-center gap-1">Price Change (%) {getSortIcon('price_change_pct')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('period_alpha')}>
                    <div className="flex items-center justify-center gap-1">Alpha (%) {getSortIcon('period_alpha')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('model_forward_trend_accuracy')}>
                    <div className="flex items-center justify-center gap-1">Trend Accuracy (%) {getSortIcon('model_forward_trend_accuracy')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('model_next_day_direction_accuracy')}>
                    <div className="flex items-center justify-center gap-1">Direction Accuracy (%) {getSortIcon('model_next_day_direction_accuracy')}</div>
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer" onClick={() => handleSort('created_at')}>
                    <div className="flex items-center justify-center gap-1">Updated (CET) {getSortIcon('created_at')}</div>
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && assetModelSummary.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No data</td></tr>
              )}
              {currentItems.map((r, idx) => (
                <tr key={`${r.symbol}-${r.model_name}-${idx}`} className={theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}>
                  <td
                    title={r.name || ''}
                    aria-label={r.name || r.symbol || ''}
                    className="px-4 py-3 text-sm font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                    onClick={(e) => {
                      if (!r.symbol) return;
                      const mkt = r.category || selectedMarket;
                      if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        openStockInNewTab(r.symbol, mkt);
                        return;
                      }
                      window.location.assign(`/summary?stock=${encodeURIComponent(r.symbol)}&market=${encodeURIComponent(String(mkt).toUpperCase())}`);
                    }}
                    onAuxClick={(e) => {
                      if (e.button === 1 && r.symbol) {
                        e.preventDefault();
                        const mkt = r.category || selectedMarket;
                        openStockInNewTab(r.symbol, mkt);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      try {
                        const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        const mkt = r.category || selectedMarket;
                        setCtxMenu({ open: true, x, y, item: { symbol: r.symbol, market: String(mkt).toUpperCase() } });
                      } catch {
                        const mkt = r.category || selectedMarket;
                        setCtxMenu({ open: true, x: e.clientX, y: e.clientY, item: { symbol: r.symbol, market: String(mkt).toUpperCase() } });
                      }
                    }}
                  >
                    {r.symbol}
                  </td>
                  <td className="px-4 py-3 text-sm">{r.model_name}</td>
                  <td className="px-4 py-3 text-sm">{formatNumber(r.period_roi, 2)}</td>
                  <td className="px-4 py-3 text-sm">{formatNumber(r.price_change_pct, 2)}</td>
                  <td className="px-4 py-3 text-sm">{formatNumber(r.period_alpha, 2)}</td>
                  <td className="px-4 py-3 text-sm">{formatNumber(r.model_forward_trend_accuracy, 1)}</td>
                  <td className="px-4 py-3 text-sm">{formatNumber(r.model_next_day_direction_accuracy, 1)}</td>
                  <td className="px-4 py-3 text-sm">{formatCreatedAt(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          {/* Context Menu */}
          {ctxMenu.open && ctxMenu.item && (
            <div
              ref={menuRef}
              className="absolute z-50 min-w-[180px] rounded-md border shadow-lg text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
              style={{
                left: Math.min(
                  ctxMenu.x,
                  (containerRef.current ? containerRef.current.clientWidth : 240) - 200
                ),
                top: Math.min(
                  ctxMenu.y,
                  (containerRef.current ? containerRef.current.clientHeight : 400) - 100
                ),
              }}
              role="menu"
            >
              <button
                type="button"
                className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  try {
                    const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.market)}`;
                    window.open(url, '_blank', 'noopener');
                  } catch {}
                  setCtxMenu({ open: false, x: 0, y: 0, item: null });
                }}
                role="menuitem"
              >
                Open in New Tab
              </button>
              <button
                type="button"
                className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  try {
                    const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.market)}`;
                    window.open(url, '_blank', 'noopener,width=1200,height=800');
                  } catch {}
                  setCtxMenu({ open: false, x: 0, y: 0, item: null });
                }}
                role="menuitem"
              >
                Open in New Window
              </button>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t sm:px-6 ${
            theme === 'dark' 
              ? 'bg-gray-900 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between flex-1 sm:hidden">
              <div className="flex space-x-2">
                <button onClick={handleFirstPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>First</button>
                <button onClick={handlePreviousPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>Previous</button>
              </div>
              <div className="flex space-x-2">
                <button onClick={handleNextPage} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>Next</button>
                <button onClick={handleLastPage} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>Last</button>
              </div>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button onClick={handleFirstPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-3 py-2 rounded-l-md text-sm font-medium ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-500 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>First</button>
                  <button onClick={handlePreviousPage} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-500 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}><span className="sr-only">Previous</span><svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg></button>
                  <span className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300 bg-gray-800' : 'text-gray-700 bg-white'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>Page {currentPage} of {totalPages}</span>
                  <button onClick={handleNextPage} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-500 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}><span className="sr-only">Next</span><svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg></button>
                  <button onClick={handleLastPage} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-3 py-2 rounded-r-md text-sm font-medium ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : theme === 'dark' ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-500 bg-white hover:bg-gray-50'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>Last</button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
